import React, { useEffect, useState, useCallback, useMemo } from "react";
import { api, formatApiError, coverUrl } from "../lib/api";
import {
  Radio, Sparkles, BicepsFlexed, Music2, BookOpenText, Sun, Disc3,
  Play, SkipForward, Square, ThumbsUp, ThumbsDown, Flame, Clock, ChevronDown, ChevronUp,
} from "lucide-react";
import { usePlayer } from "../context/PlayerContext";
import NowPlayingStage from "../components/NowPlayingStage";

const STATION_ICONS = {
  "truth-hiphop": Disc3,
  "worship": Sparkles,
  "study-instrumentals": BookOpenText,
  "sabbath-flow": Sun,
  "new-submissions": Music2,
  "most-liked": BicepsFlexed,
  "recently-approved": Radio,
};

const GRADIENTS = {
  "truth-hiphop": "from-[#3a2a1a] to-[#0a0a14]",
  "worship": "from-[#1a1a3a] to-[#0a0a14]",
  "study-instrumentals": "from-[#1a3a3a] to-[#0a0a14]",
  "sabbath-flow": "from-[#3a3019] to-[#0a0a14]",
  "new-submissions": "from-[#1f2630] to-[#0a0a14]",
  "most-liked": "from-[#3a1a2a] to-[#0a0a14]",
  "recently-approved": "from-[#2a2a1a] to-[#0a0a14]",
};

const DEFAULT_COVER = "https://images.unsplash.com/photo-1771747131849-41056d3d9be3?crop=entropy&cs=srgb&fm=jpg&w=600&q=80";

function NowPlayingHero() {
  const { currentTrack } = usePlayer();
  const empty = (
    <div data-testid="now-playing-hero-empty" className="card-surface p-8 md:p-12 bg-gradient-to-br from-[#1a1a2a] to-[#0a0a14]">
      <div className="flex flex-col md:flex-row items-center md:items-start gap-6">
        <div className="w-32 h-32 md:w-40 md:h-40 rounded-xl border border-[#22262E] bg-[#111317] flex items-center justify-center flex-shrink-0">
          <Radio className="w-12 h-12 md:w-16 md:h-16 text-[#C5A059]/60" strokeWidth={1.2} />
        </div>
        <div className="flex-1 text-center md:text-left">
          <div className="overline text-[#C5A059] mb-2">Live radio hub</div>
          <h2 className="font-serif text-3xl md:text-4xl text-[#FDFBF7] tracking-tight">
            Choose a station and Yah-Wave will keep the music flowing.
          </h2>
          <p className="text-[#A1A4AB] mt-3 text-sm md:text-base max-w-2xl">
            Pick a vibe below — every station builds a Pandora-style queue from approved submissions.
            Use the thumbs to shape what plays next.
          </p>
        </div>
      </div>
    </div>
  );
  return (
    <NowPlayingStage
      isActive={!!currentTrack}
      emptyState={empty}
      testIdPrefix="hero"
    />
  );
}

function ShapeStationPanel() {
  return (
    <div data-testid="shape-station-panel" className="card-surface p-5 bg-[#0E1014]">
      <div className="overline text-[#C5A059] mb-3 flex items-center gap-2">
        <Sparkles className="w-3.5 h-3.5" strokeWidth={1.5} /> Shape this station
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
        <div className="flex items-start gap-2">
          <ThumbsUp className="w-4 h-4 text-[#C5A059] flex-shrink-0 mt-0.5" strokeWidth={1.5} />
          <div>
            <div className="text-[#FDFBF7]">Thumbs up</div>
            <div className="text-[#6C707A] text-xs">More like this</div>
          </div>
        </div>
        <div className="flex items-start gap-2">
          <ThumbsDown className="w-4 h-4 text-red-300 flex-shrink-0 mt-0.5" strokeWidth={1.5} />
          <div>
            <div className="text-[#FDFBF7]">Thumbs down</div>
            <div className="text-[#6C707A] text-xs">Skip and play less of this</div>
          </div>
        </div>
        <div className="flex items-start gap-2">
          <SkipForward className="w-4 h-4 text-[#A1A4AB] flex-shrink-0 mt-0.5" strokeWidth={1.5} />
          <div>
            <div className="text-[#FDFBF7]">Skip</div>
            <div className="text-[#6C707A] text-xs">Jump to next track</div>
          </div>
        </div>
        <div className="flex items-start gap-2">
          <Square className="w-4 h-4 text-red-300 flex-shrink-0 mt-0.5" strokeWidth={2} fill="currentColor" />
          <div>
            <div className="text-[#FDFBF7]">Stop</div>
            <div className="text-[#6C707A] text-xs">Fully halt the station</div>
          </div>
        </div>
      </div>
    </div>
  );
}

function TrackStrip({ title, icon: Icon, tracks, testId, onPlay, busy, emptyHint }) {
  if (!tracks?.length) {
    return (
      <section data-testid={testId} className="space-y-3">
        <h3 className="font-serif text-xl text-[#FDFBF7] flex items-center gap-2">
          <Icon className="w-4 h-4 text-[#C5A059]" strokeWidth={1.5} /> {title}
        </h3>
        <div className="card-surface p-6 text-center text-[#6C707A] text-sm">{emptyHint}</div>
      </section>
    );
  }
  return (
    <section data-testid={testId} className="space-y-3">
      <h3 className="font-serif text-xl text-[#FDFBF7] flex items-center gap-2">
        <Icon className="w-4 h-4 text-[#C5A059]" strokeWidth={1.5} /> {title}
      </h3>
      <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1 scroll-px-1 snap-x">
        {tracks.map((t) => {
          const cover = coverUrl(t) || DEFAULT_COVER;
          return (
            <button
              key={t.id}
              onClick={() => onPlay(t, tracks)}
              disabled={busy}
              data-testid={`${testId}-card-${t.id}`}
              className="group relative flex-shrink-0 w-44 snap-start rounded-lg overflow-hidden border border-[#22262E] hover:border-[#C5A059]/50 bg-[#0E1014] transition-all text-left"
            >
              <div className="aspect-square relative overflow-hidden">
                <img src={cover} alt="" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                <div className="absolute bottom-2 right-2 w-9 h-9 rounded-full bg-[#C5A059] text-[#060709] flex items-center justify-center opacity-0 group-hover:opacity-100 translate-y-1 group-hover:translate-y-0 transition-all shadow-[0_0_18px_rgba(197,160,89,0.4)]">
                  <Play className="w-4 h-4 ml-0.5" strokeWidth={2} />
                </div>
              </div>
              <div className="p-3">
                <div className="text-[#FDFBF7] text-sm font-medium truncate">{t.title}</div>
                <div className="text-[#A1A4AB] text-xs truncate">{t.artist}</div>
              </div>
            </button>
          );
        })}
      </div>
    </section>
  );
}

function CollapsibleStationsGrid({ stations, loading, playStation, busyId, stationPreview }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <section className="space-y-4" data-testid="stations-section">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="overline text-[#C5A059] mb-1">More to spin</div>
          <h2 className="font-serif text-2xl text-[#FDFBF7] tracking-tight">Pick a different vibe</h2>
        </div>
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="text-xs overline px-3 py-1.5 rounded-full border border-[#22262E] text-[#A1A4AB] hover:border-[#C5A059]/40 hover:text-[#FDFBF7] inline-flex items-center gap-1.5"
          data-testid="stations-grid-toggle"
        >
          {expanded ? (<><ChevronUp className="w-3.5 h-3.5" strokeWidth={1.5} /> Hide stations</>) : (<><ChevronDown className="w-3.5 h-3.5" strokeWidth={1.5} /> {stations.length} stations</>)}
        </button>
      </div>
      {expanded && (
        loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {Array.from({ length: 6 }).map((_, i) => <div key={i} className="aspect-[3/2] rounded-xl shimmer" />)}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="stations-grid">
            {stations.map((s) => {
              const Icon = STATION_ICONS[s.id] || Radio;
              const grad = GRADIENTS[s.id] || "from-[#1a1a2a] to-[#0a0a14]";
              const empty = s.track_count === 0;
              const preview = stationPreview[s.id];
              return (
                <div
                  key={s.id}
                  data-testid={`station-card-${s.id}`}
                  className={`group relative overflow-hidden rounded-xl border border-[#22262E] hover:border-[#C5A059]/50 transition-all duration-300 ${empty ? "opacity-60" : ""}`}
                >
                  <div className={`bg-gradient-to-br ${grad} p-5 min-h-[180px] flex flex-col justify-between`}>
                    <div className="flex items-start justify-between">
                      <Icon className="w-7 h-7 text-[#C5A059]" strokeWidth={1.5} />
                      <span className="overline text-[10px] text-[#A1A4AB]">{s.track_count} track{s.track_count !== 1 ? "s" : ""}</span>
                    </div>
                    <div>
                      <div className="font-serif text-2xl text-[#FDFBF7] tracking-tight">{s.name}</div>
                      <div className="text-sm text-[#A1A4AB] mt-1">{s.tagline}</div>
                      {preview && (
                        <div className="mt-3 text-xs text-[#6C707A] truncate flex items-center gap-1.5">
                          <Music2 className="w-3 h-3" strokeWidth={1.5} />
                          <span className="truncate">Now spinning: <span className="text-[#A1A4AB]">{preview.title}</span></span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="absolute inset-x-0 bottom-0 p-3 flex items-center justify-between bg-gradient-to-t from-[#060709] to-transparent">
                    <button
                      onClick={() => !empty && playStation(s)}
                      disabled={empty || busyId === s.id}
                      data-testid={`station-card-play-${s.id}`}
                      className="px-4 py-1.5 rounded-full bg-[#C5A059] text-[#060709] text-xs font-semibold hover:bg-[#DCC286] disabled:opacity-30 disabled:cursor-not-allowed transition-colors flex items-center gap-1.5 shadow-[0_0_18px_rgba(197,160,89,0.3)]"
                    >
                      {busyId === s.id ? "Loading…" : (<><Play className="w-3 h-3 ml-0.5" strokeWidth={2.5} /> Play Station</>)}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )
      )}
    </section>
  );
}


export default function StationsPage() {
  const [stations, setStations] = useState([]);
  const [recentlyPlayed, setRecentlyPlayed] = useState([]);
  const [trending, setTrending] = useState([]);
  const [recentlyApproved, setRecentlyApproved] = useState([]);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const { playQueue, currentTrack } = usePlayer();

  const refresh = useCallback(async () => {
    setLoading(true); setErr("");
    try {
      const [s, rp, mlq, raq] = await Promise.all([
        api.get("/stations"),
        api.get("/tracks/recently-played", { params: { limit: 12 } }),
        api.get("/stations/most-liked/queue", { params: { limit: 12 } }),
        api.get("/stations/recently-approved/queue", { params: { limit: 12 } }),
      ]);
      setStations(s.data || []);
      setRecentlyPlayed(rp.data || []);
      setTrending(mlq.data || []);
      setRecentlyApproved(raq.data || []);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);
  // Refresh recently-played when current track changes (so the strip stays live)
  useEffect(() => {
    if (!currentTrack) return;
    const t = setTimeout(() => {
      api.get("/tracks/recently-played", { params: { limit: 12 } })
        .then((r) => setRecentlyPlayed(r.data || []))
        .catch(() => {});
    }, 1500);
    return () => clearTimeout(t);
  }, [currentTrack?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const playStation = useCallback(async (s) => {
    setBusyId(s.id); setErr("");
    try {
      const { data } = await api.get(`/stations/${s.id}/queue`, { params: { limit: 40 } });
      if (!data || data.length === 0) {
        setErr(`No tracks yet for "${s.name}". Approve some submissions first.`);
        setBusyId(null);
        return;
      }
      playQueue(data, 0, "station");
      window.dispatchEvent(new CustomEvent("yahweh-station", { detail: { id: s.id, name: s.name } }));
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setBusyId(null);
    }
  }, [playQueue]);

  const playSingleAsQueue = useCallback((track, queueOfTracks) => {
    const idx = queueOfTracks.findIndex((t) => t.id === track.id);
    playQueue(queueOfTracks, Math.max(0, idx), "normal");
  }, [playQueue]);

  // Map of station_id → preview track (most-played match, best-effort)
  const stationPreview = useMemo(() => {
    const m = {};
    for (const t of recentlyPlayed) {
      if (t.station && !m[t.station]) m[t.station] = t;
    }
    return m;
  }, [recentlyPlayed]);

  return (
    <div className="space-y-10">
      <NowPlayingHero />

      {err && <div className="text-red-400 text-sm" data-testid="stations-error">{err}</div>}

      <ShapeStationPanel />

      <TrackStrip
        title="Recently Played"
        icon={Clock}
        tracks={recentlyPlayed}
        testId="recently-played-strip"
        onPlay={playSingleAsQueue}
        busy={loading}
        emptyHint="Nothing has been played yet. Pick a station above to start the day."
      />

      <TrackStrip
        title="Trending — Most Liked"
        icon={Flame}
        tracks={trending.filter((t) => (t.like_count || 0) > 0)}
        testId="trending-strip"
        onPlay={playSingleAsQueue}
        busy={loading}
        emptyHint="No likes logged yet — be the first to thumbs-up a track!"
      />

      <TrackStrip
        title="Recently Approved"
        icon={Sparkles}
        tracks={recentlyApproved}
        testId="recently-approved-strip"
        onPlay={playSingleAsQueue}
        busy={loading}
        emptyHint="No approved tracks yet."
      />

      <CollapsibleStationsGrid
        stations={stations}
        loading={loading}
        playStation={playStation}
        busyId={busyId}
        stationPreview={stationPreview}
      />
    </div>
  );
}
